import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Scrim, Modal, Btn, IconBtn, Input } from './primitives';
import { X, AlertCircle } from 'lucide-react';

/** Narrow confirm shell — destructive dialogs stay compact and scannable. */
const ConfirmModal = styled(Modal)`
  max-width: 460px;

  /* No subtitle in confirm dialogs: vertically center the title with the close
     button so the divider sits tight against the header instead of floating. */
  .modal__head {
    align-items: center;
    padding: 20px 24px;
  }

  .modal__body {
    padding: 20px 24px;
  }

  .modal__foot {
    padding: 16px 24px;
  }
`;

const BodyMessage = styled.p`
  margin: 0 0 18px;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
  line-height: 1.55;
`;

const DangerBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 11px 13px;
  margin-bottom: 18px;
  border-radius: ${({ theme }) => theme.rMd};
  background: color-mix(in oklch, ${({ theme }) => theme.danger} 8%, ${({ theme }) => theme.surface});
  border: 1px solid color-mix(in oklch, ${({ theme }) => theme.danger} 18%, transparent);
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.text};
  line-height: 1.5;

  svg {
    flex: none;
    margin-top: 1px;
    color: ${({ theme }) => theme.danger};
  }
`;

const TypedSection = styled.div`
  margin-top: 0;
`;

const TypedLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fsSm};
  font-weight: 650;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.text};
`;

const TypedInput = styled(Input)`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace;
  font-size: ${({ theme }) => theme.fsSm};
  letter-spacing: 0.02em;
  background: ${({ theme }) => theme.surface3};

  ${({ $invalid, theme }) => $invalid && `
    border-color: color-mix(in oklch, ${theme.danger} 45%, ${theme.border});
    background: color-mix(in oklch, ${theme.danger} 4%, ${theme.surface});
  `}
`;

const TypedHint = styled.p`
  margin: 8px 0 0;
  font-size: ${({ theme }) => theme.fsMicro};
  color: ${({ theme }) => theme.textSubtle};
  line-height: 1.45;

  strong {
    font-weight: 650;
    color: ${({ theme }) => theme.text};
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace;
  }
`;

const TypedError = styled.p`
  margin: 8px 0 0;
  font-size: ${({ theme }) => theme.fsMicro};
  color: ${({ theme }) => theme.danger};
  line-height: 1.45;
`;

const PrimaryBtn = styled(Btn)`
  &:disabled,
  &[aria-disabled='true'] {
    opacity: 0.42;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

/**
 * AWS-style confirmation dialog — user must type "confirm" before proceeding.
 *
 * Prefer invoking via `useConfirm()` from `ConfirmProvider` rather than
 * rendering this component directly:
 *
 *   const askConfirmation = useConfirm();
 *   const ok = await askConfirmation({
 *     title: 'Disable course?',
 *     message: 'Students will no longer access this course.',
 *     confirmLabel: 'Disable',
 *     variant: 'danger',
 *     requireTypedConfirm: true, // default
 *     confirmText: 'confirm',    // default
 *   });
 *   if (ok) { ... }
 */
export default function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  requireTypedConfirm = true,
  confirmText = 'confirm',
  onCancel,
  onConfirm,
}) {
  const [typedValue, setTypedValue] = useState('');
  const modalRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, []);

  useEffect(() => {
    const modalEl = modalRef.current;
    if (!modalEl) return undefined;

    const focusTarget = requireTypedConfirm ? inputRef.current : modalEl.querySelector('button');
    focusTarget?.focus();

    const getFocusables = () => Array.from(
      modalEl.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    );

    const handleTab = (event) => {
      if (event.key !== 'Tab') return;

      const focusables = getFocusables();
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    modalEl.addEventListener('keydown', handleTab);
    return () => modalEl.removeEventListener('keydown', handleTab);
  }, [requireTypedConfirm]);

  const isTypedMatch = typedValue.trim().toLowerCase() === confirmText.toLowerCase();
  const hasPartialInput = typedValue.length > 0 && !isTypedMatch;
  const canConfirm = !requireTypedConfirm || isTypedMatch;
  const confirmVariant = variant === 'danger' ? 'danger' : 'subtle';

  const describedByIds = [
    message ? 'confirm-dialog-message' : null,
    requireTypedConfirm ? 'confirm-dialog-typed-hint' : null,
    hasPartialInput ? 'confirm-dialog-typed-error' : null,
  ].filter(Boolean).join(' ') || undefined;

  const handleConfirmClick = useCallback(() => {
    if (!canConfirm) return;
    onConfirm();
  }, [canConfirm, onConfirm]);

  const handleCancelClick = useCallback(() => {
    setTypedValue('');
    onCancel();
  }, [onCancel]);

  // Backdrop-only handlers: preventDefault on bubbled modal mousedown would block
  // confirm/cancel button clicks, leaving askConfirmation() pending forever.
  const handleScrimMouseDown = (event) => {
    if (event.target === event.currentTarget) {
      event.preventDefault();
    }
  };

  const handleScrimClick = (event) => {
    if (event.target === event.currentTarget) {
      handleCancelClick();
    }
  };

  const stopMouseDownBubble = (event) => event.stopPropagation();

  return createPortal(
    <Scrim onMouseDown={handleScrimMouseDown} onClick={handleScrimClick} role="presentation">
      <ConfirmModal
        ref={modalRef}
        onMouseDown={stopMouseDownBubble}
        onClick={(event) => event.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby={describedByIds}
      >
        <div className="modal__head">
          <div>
            <h3 id="confirm-dialog-title">{title}</h3>
          </div>
          <IconBtn
            className="x-btn"
            type="button"
            onMouseDown={stopMouseDownBubble}
            onClick={handleCancelClick}
            aria-label="Close dialog"
          >
            <X size={18} />
          </IconBtn>
        </div>

        <div className="modal__body">
          {variant === 'danger' && (
            <DangerBanner role="note">
              <AlertCircle size={16} aria-hidden="true" />
              <span>This action may have significant consequences and cannot be easily undone.</span>
            </DangerBanner>
          )}

          {message && (
            <BodyMessage id="confirm-dialog-message">
              {message}
            </BodyMessage>
          )}

          {requireTypedConfirm && (
            <TypedSection>
              <TypedLabel htmlFor="confirm-dialog-input">
                Type confirm to proceed
              </TypedLabel>
              <TypedInput
                ref={inputRef}
                id="confirm-dialog-input"
                type="text"
                autoComplete="off"
                spellCheck={false}
                aria-label="Type confirm to proceed"
                aria-invalid={hasPartialInput}
                aria-describedby={[
                  'confirm-dialog-typed-hint',
                  hasPartialInput ? 'confirm-dialog-typed-error' : null,
                ].filter(Boolean).join(' ') || undefined}
                placeholder={confirmText}
                value={typedValue}
                $invalid={hasPartialInput}
                onChange={(event) => setTypedValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleConfirmClick();
                  }
                }}
              />
              <TypedHint id="confirm-dialog-typed-hint">
                Enter <strong>{confirmText}</strong> exactly to enable the action.
              </TypedHint>
              {hasPartialInput && (
                <TypedError id="confirm-dialog-typed-error" role="alert">
                  Text does not match — type <strong>{confirmText}</strong> to continue.
                </TypedError>
              )}
            </TypedSection>
          )}
        </div>

        <div className="modal__foot">
          <Btn
            type="button"
            $variant="ghost"
            onMouseDown={stopMouseDownBubble}
            onClick={handleCancelClick}
          >
            {cancelLabel}
          </Btn>
          <PrimaryBtn
            type="button"
            $variant={confirmVariant}
            onMouseDown={stopMouseDownBubble}
            onClick={handleConfirmClick}
            disabled={!canConfirm}
            aria-disabled={!canConfirm}
          >
            {confirmLabel}
          </PrimaryBtn>
        </div>
      </ConfirmModal>
    </Scrim>,
    document.body,
  );
}
